import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  View,
  StyleSheet,
  Modal,
  TouchableOpacity,
  Image,
  Alert,
  Text,
  ScrollView,
  Platform,
} from "react-native";
import {
  BottomButtons,
  BtnCloseModal,
  LightBtn,
  ProfileChipPng,
  SmallVipSix,
} from "../../../assets";
import { useDevice } from "../../../utils/hooks/useDevice";
import { ButtonCard } from "../Profile/ButtonCard";
import ChipScreen from "../Profile/BuyChips/ProfileChipScreen";
import VipScreen from "../Profile/BuyVip/ProfileVipScreen";
import { RootStackParamList } from "../../navigator";
import { RouteProp } from "@react-navigation/native";
import {
  packagesRequest,
  paymentRequest,
  paymentReset,
} from "../../../store/packages/packagesActions";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../store";
import { Package } from "../../../store/types";
import { useTranslation } from "react-i18next";
import PaymentProviderSelector, {
  PaymentProvider,
} from "./PaymentProviderSelector";
import { PaymentStatusModal } from "./PaymentStatusModal";
import { updateUserVipStatus } from "../../../store/auth/authActions";
import { analyticsEvents } from "../../../services/firebase/analytics-events.service";
import { cleverTapService } from "../../../services/clevertap/clevertap.service";
import { deltaDNAService } from "../../../services/deltaDNA/deltadna.service";
import { env } from "../../../utils/env";
import { PaymentWebViewModal } from "./PaymentWebViewModal";
import { usePaymentStatus } from "../../../utils/hooks/paymentStatus";
import { useIAP } from "../../../utils/hooks/useIAP";
import i18n from "../../../i18n/i18n";
import LS from "../../../utils/localStorageUtils";

type BuyPackagesRouteProp = RouteProp<RootStackParamList, "BuyPackages">;

interface IProps {
  onClose: () => void;
  route: BuyPackagesRouteProp;
}

function BuyPackagesModal({ onClose, route }: IProps) {
  const { t } = useTranslation();
  const isNative = Platform.OS !== "web";
  const { isPortrait, isDesktop, width } = useDevice();

  const dispatch = useDispatch();
  const packageState = useSelector((state: RootState) => state.package);
  const paymentData = usePaymentStatus();

  const hasTrackedRef = useRef(false);

  const currentLanguage = i18n.language || "bg";

  // ✅ Handle user data for both web and native
  const getUserData = () => {
    if (Platform.OS === "web") {
      return JSON.parse(localStorage.getItem("auth_user") || "{}");
    }
    return {};
  };
  const user = getUserData();

  const { items, paymentError, payment } = packageState;

  // -------------------------
  // IAP Integration (Native only)
  // -------------------------
  const iap = useIAP();

  // -------------------------
  // Provider selection state
  // -------------------------
  const [selectedProvider, setSelectedProvider] =
    useState<PaymentProvider>("stripe");

  // ✅ Web-only provider modal flow
  const [showProviderModal, setShowProviderModal] = useState(false);
  const [pendingPackage, setPendingPackage] = useState<Package | null>(null);

  // -------------------------
  // Payment flow state
  // -------------------------
  const [purchasedPackage, setPurchasedPackage] = useState<Package | null>(
    null,
  );

  const [showPaymentWebView, setShowPaymentWebView] = useState(false);
  const [paymentCheckoutUrl, setPaymentCheckoutUrl] = useState("");
  const [showPaymentStatus, setShowPaymentStatus] = useState(false);
  const [paymentStatusType, setPaymentStatusType] = useState<
    "success" | "cancelled" | "error"
  >("success");
  const [nativePaymentPackage, setNativePaymentPackage] =
    useState<Package | null>(null);

  // -------------------------
  // Initial effects
  // -------------------------
  useEffect(() => {
    dispatch(paymentReset());
  }, [dispatch]);

  useEffect(() => {
    if (hasTrackedRef.current) return;

    hasTrackedRef.current = true;
    analyticsEvents.trackOpenShop();
    cleverTapService.trackEvent(`Opened shop`, { testShop: "test" });
  }, []);

  useEffect(() => {
    if (hasTrackedRef.current) {
      console.log("⏭️ Already tracked, skipping...");
      return;
    }

    const trackShopOpen = async () => {
      hasTrackedRef.current = true;
      console.log("opening shop");
      analyticsEvents.trackOpenShop();
      cleverTapService.trackEvent(`Opened shop`, {
        testShop: "test",
      });

      await deltaDNAService.openShopEvent(
        Platform.OS,
        currentLanguage,
        user.level,
        0,
        user.chips,
        "regular",
        "chips",
        1,
      );
    };

    trackShopOpen();
  }, []);

  useEffect(() => {
    if (paymentError) {
      Alert.alert(t("T_ERROR") || "Error", paymentError, [
        { text: t("T_OK") || "OK" },
      ]);
    }
  }, [paymentError, t]);

  useEffect(() => {
    dispatch(packagesRequest({ start: 0, limit: 20 }));
  }, [dispatch]);

  useEffect(() => {
    console.log("[BuyPackages] Payment data:", paymentData);

    if (paymentData.status === "success" && paymentData.packageInfo) {
      console.log("Payment successful for package:", paymentData.packageInfo);

      const pkg = paymentData.packageInfo;

      let vipDurationDays: number | null = null;
      let offerType: number;

      if (pkg.type === "VIP_MEMBERSHIP") {
        if (pkg.price === 6.49) {
          vipDurationDays = 30;
        } else if (pkg.price === 19.99) {
          vipDurationDays = 180;
        } else {
          vipDurationDays = 365;
        }
        offerType = 1;
      } else {
        offerType = 2;
      }

      // Analytics tracking
      // analyticsEvents.trackPurchase({
      //   id: pkg.id,
      //   price: pkg.price,
      //   currency: Array.isArray(pkg.currency)
      //     ? pkg.currency[0]
      //     : pkg.currency,
      // });

      cleverTapService.trackEvent(`${pkg.type} Purchased`, {
        package_id: pkg.id,
        price: pkg.price,
        currency: pkg.currency,
      });

      // DeltaDNA tracking
      deltaDNAService.playerPaymentEvent(
        Platform.OS,
        user.chips,
        currentLanguage,
        Array.isArray(pkg.currency) ? pkg.currency[0] : pkg.currency,
        pkg.price || 0,
        selectedProvider,
        pkg.id || "",
        pkg.id || "",
        pkg.name || "",
        vipDurationDays || pkg.chipAmount || 0,
        offerType,
      );

      if (pkg.type === "VIP_MEMBERSHIP") {
        dispatch(updateUserVipStatus({ isVip: true }));

        Alert.alert(
          t("T_SUCCESS") || "Success",
          t("T_VIP_PURCHASE_SUCCESS") ||
            "VIP membership activated successfully!",
          [
            {
              text: t("T_OK") || "OK",
              onPress: () => onClose(),
            },
          ],
        );
      } else if (pkg.type === "CHIPS") {
        Alert.alert(
          t("T_SUCCESS") || "Success",
          t("T_CHIPS_PURCHASE_SUCCESS") || "Chips purchased successfully!",
          [
            {
              text: t("T_OK") || "OK",
            },
          ],
        );
      }
    } else {
      Alert.alert(
        t("T_ERROR") || "Error",
        t("T_PAYMENT_FAILED") || "Payment failed. Please try again.",
        [{ text: t("T_OK") || "OK" }],
      );
    }
  }, [paymentData, dispatch, t, onClose]);

  // ✅ Native: open WebView automatically when checkoutUrl arrives
  useEffect(() => {
    if (Platform.OS !== "web" && payment?.checkoutUrl) {
      setPaymentCheckoutUrl(payment.checkoutUrl);
      setShowPaymentWebView(true);
    }
  }, [payment?.checkoutUrl]);

  // ✅ Web: listen for redirect result / payment status hook
  useEffect(() => {
    if (Platform.OS !== "web") return;

    if (paymentData.status === "success" && paymentData.packageInfo) {
      handlePaymentComplete(paymentData.packageInfo as Package, "success");
    } else if (paymentData.status === "cancelled") {
      Alert.alert(
        t("T_CANCELLED") || "Cancelled",
        t("T_PAYMENT_CANCELLED") || "Payment was cancelled.",
        [{ text: t("T_OK") || "OK" }],
      );
    } else if (!paymentData.status) {
      Alert.alert(
        t("T_ERROR") || "Error",
        t("T_PAYMENT_FAILED") || "Payment failed. Please try again.",
        [{ text: t("T_OK") || "OK" }],
      );
    }
  }, [paymentData, t]);

  // -------------------------
  // Helpers
  // -------------------------
  const getFrontendUrl = (): string => {
    if (Platform.OS !== "web") {
      return env("SVARA_WEB_URL") || "https://t-svara.bowa.awd-group.tech";
    }

    try {
      const w = window as any;
      const origin =
        w?.location && typeof w.location.origin === "string"
          ? w.location.origin
          : "";
      return (
        origin || env("SVARA_WEB_URL") || "https://t-svara.bowa.awd-group.tech"
      );
    } catch {
      return env("SVARA_WEB_URL") || "https://t-svara.bowa.awd-group.tech";
    }
  };

  const frontendUrl = getFrontendUrl();

  const formatPackageInfoForModal = () => {
    const pkg = nativePaymentPackage || purchasedPackage;
    if (!pkg) return undefined;

    let vipDurationDays: number | null = null;

    if (pkg.type === "VIP_MEMBERSHIP") {
      if (pkg.price === 6.49) vipDurationDays = 30;
      else if (pkg.price === 19.99) vipDurationDays = 180;
      else vipDurationDays = 365;

      return {
        type: "VIP_MEMBERSHIP",
        name: pkg.name,
        vip: { durationDays: vipDurationDays || 0 },
      };
    }

    if (pkg.type === "CHIPS") {
      return {
        type: "CHIPS",
        name: pkg.name,
        chips: pkg.chipAmount || (pkg as any).chips || 0,
      };
    }

    return undefined;
  };

  // -------------------------
  // Payment logic (unchanged)
  // -------------------------
  const handlePayment = (pkg: Package) => {
    setPurchasedPackage(pkg);
    setNativePaymentPackage(pkg);
    analyticsEvents.trackBeginCheckout(pkg.id);

    dispatch(
      paymentRequest({
        amount: Number(pkg.price || 0),
        pkg,
        currency:
          (pkg as any)?.currency?.name || (pkg as any)?.currency || "EUR",
        frontendUrl,
        provider: selectedProvider,
      }),
    );
  };

  // ✅ New: web-only provider gate, native uses IAP
  const handleBuyPress = (pkg: Package) => {
    if (Platform.OS === "web") {
      setPendingPackage(pkg);
      setShowProviderModal(true);
      return;
    }

    // Native: Use IAP if supported, otherwise fallback to web payment
    if (iap.isSupported && iap.isInitialized) {
      console.log("[BuyPackages] Using IAP for purchase:", pkg);
      setPurchasedPackage(pkg);
      setNativePaymentPackage(pkg);
      analyticsEvents.trackBeginCheckout(pkg.id);
      iap.purchasePackage(pkg);
    } else {
      console.log("[BuyPackages] IAP not available, using web payment:", pkg);
      handlePayment(pkg);
    }
  };

  const handleProviderContinue = () => {
    setShowProviderModal(false);
    if (pendingPackage) handlePayment(pendingPackage);
    setPendingPackage(null);
  };

  const handleProviderClose = () => {
    setShowProviderModal(false);
    setPendingPackage(null);
  };

  const handlePaymentComplete = (
    pkg: Package,
    status: "success" | "cancelled" | "error",
  ) => {
    if (status !== "success") return;

    let vipDurationDays: number | null = null;

    if (pkg.type === "VIP_MEMBERSHIP") {
      if (pkg.price === 6.49) vipDurationDays = 30;
      else if (pkg.price === 19.99) vipDurationDays = 180;
      else vipDurationDays = 365;

      dispatch(updateUserVipStatus({ isVip: true }));
    }

    cleverTapService.trackEvent(`${pkg.type} Purchased`, {
      package_id: pkg.id,
      price: pkg.price,
      currency: (pkg as any)?.currency,
    });
  };

  const handlePaymentSuccess = (depositId: string) => {
    setShowPaymentWebView(false);

    let pkg = purchasedPackage;

    if (depositId) {
      try {
        const savedPackageJson = LS.get(`package_${depositId}`);
        if (savedPackageJson) {
          pkg = JSON.parse(savedPackageJson);
          LS.remove(`package_${depositId}`);
        }
      } catch {}
    }

    if (pkg) {
      handlePaymentComplete(pkg, "success");
      setNativePaymentPackage(pkg);
      setPaymentStatusType("success");
      setShowPaymentStatus(true);
    }

    dispatch(paymentReset());
    dispatch(packagesRequest({ start: 0, limit: 20 }));
  };

  const handlePaymentCancel = (depositId: string) => {
    setShowPaymentWebView(false);

    let pkg = purchasedPackage;
    if (depositId) {
      try {
        const savedPackageJson = LS.get(`package_${depositId}`);
        if (savedPackageJson) {
          pkg = JSON.parse(savedPackageJson);
          LS.remove(`package_${depositId}`);
        }
      } catch {}
    }

    if (pkg) setNativePaymentPackage(pkg);

    setPaymentStatusType("cancelled");
    setShowPaymentStatus(true);
    dispatch(paymentReset());
  };

  const handleCloseWebView = () => {
    setShowPaymentWebView(false);
    dispatch(paymentReset());
  };

  const handleClosePaymentStatus = () => {
    setShowPaymentStatus(false);
    setNativePaymentPackage(null);
    dispatch(paymentReset());
    if (paymentStatusType === "success") onClose();
  };

  // -------------------------
  // UI state
  // -------------------------
  const [activeTab, setActiveTab] = useState<"chips" | "vip">("chips");
  useEffect(() => {
    if (route.params?.activeTab) setActiveTab(route.params.activeTab);
  }, [route.params?.activeTab]);

  const chipPackages = useMemo(
    () =>
      items
        .filter((item) => item.type === "CHIPS")
        .sort((a, b) => (a?.price || 0) - (b?.price || 0)),
    [items],
  );

  const vipPackages = useMemo(
    () =>
      items
        .filter((item) => item.type === "VIP_MEMBERSHIP")
        .sort((a, b) => (a?.price || 0) - (b?.price || 0))
        .map((item) => ({ ...item, name: item.name.replace(/VIP/gi, "") })),
    [items],
  );

  return (
    <>
      <Modal transparent visible>
        <View
          style={[
            styles.modalOverlay,
            { minWidth: isDesktop ? 800 : 300, gap: 0 },
          ]}
        >
          {/* TOP BAR */}
          <View
            style={[styles.topButtons, { width: isPortrait ? "90%" : "60%" }]}
          >
            <View
              style={{
                width: isNative ? width * 0.38 : isDesktop ? 180 : 150,
                height: isNative ? 46 : 50,
              }}
            >
              <ButtonCard
                activeImage={LightBtn}
                inactiveImage={BottomButtons}
                label={t("T_SITE_BUY_VIP")}
                onPress={() => setActiveTab("vip")}
                active={activeTab === "vip"}
                icon={SmallVipSix}
                iconStyle={{ width: 24, height: 24 }}
                minWidth={isNative ? 130 : isDesktop ? 240 : 140}
                labelStyle={{ paddingBottom: 0, paddingLeft: 6 }}
              />
            </View>

            <View
              style={{
                width: isNative ? width * 0.38 : 140,
                height: isNative ? 46 : 50,
              }}
            >
              <ButtonCard
                activeImage={LightBtn}
                inactiveImage={BottomButtons}
                label={t("T_SITE_PROFILE_BUY")}
                onPress={() => setActiveTab("chips")}
                active={activeTab === "chips"}
                icon={ProfileChipPng}
                iconStyle={{ width: 24, height: 24 }}
                minWidth={isNative ? 130 : isDesktop ? 260 : 150}
                labelStyle={{ paddingBottom: 0, paddingHorizontal: 6 }}
              />
            </View>

            {/* CLOSE BUTTON */}
            <TouchableOpacity
              onPress={onClose}
              style={[
                styles.closeBtn,
                isNative ? styles.closeBtnNative : styles.closeBtnWeb,
              ]}
            >
              <Image source={BtnCloseModal} style={styles.closeIcon} />
            </TouchableOpacity>
          </View>

          {/* MODAL BODY */}
          <View
            style={[
              styles.modalContainer,
              {
                width: isPortrait ? "90%" : "55%",
                height: isPortrait ? "90%" : "90%",
              },
            ]}
          >
            <ScrollView
              style={styles.scrollContainer}
              contentContainerStyle={styles.scrollContent}
              showsVerticalScrollIndicator
              nestedScrollEnabled
            >
              {activeTab === "chips" && (
                <ChipScreen
                  chipPackages={chipPackages}
                  onPayment={handleBuyPress} // ✅ important
                />
              )}

              {activeTab === "vip" && (
                <VipScreen
                  vipPackages={vipPackages}
                  onPayment={handleBuyPress} // ✅ important
                />
              )}

              {paymentError && (
                <View style={styles.errorBox}>
                  <Text style={styles.errorText}>{paymentError}</Text>
                </View>
              )}
            </ScrollView>
          </View>
        </View>
      </Modal>

      {/* ✅ WEB ONLY: Provider selection modal */}
      {Platform.OS === "web" && (
        <Modal transparent visible={showProviderModal} animationType="fade">
          <View style={styles.providerOverlay}>
            <View style={styles.providerBox}>
              <TouchableOpacity
                onPress={handleProviderClose}
                style={styles.providerClose}
              >
                <Image
                  source={BtnCloseModal}
                  style={styles.providerCloseIcon}
                />
              </TouchableOpacity>

              {/* <Text style={styles.providerTitle}>{t("T_SITE_SHOP_CHOOSE_PAYMENT_METHOD")}</Text> */}

              <PaymentProviderSelector
                selectedProvider={selectedProvider}
                onSelectProvider={setSelectedProvider}
              />

              <TouchableOpacity
                onPress={handleProviderContinue}
                style={styles.providerContinueBtn}
                activeOpacity={0.8}
              >
                <Text style={styles.providerContinueText}>
                  {t("T_SITE_MODAL_BONUS_CONTINUE")}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </Modal>
      )}

      {/* ✅ Native: Payment WebView Modal */}
      {Platform.OS !== "web" && (
        <PaymentWebViewModal
          visible={showPaymentWebView}
          checkoutUrl={paymentCheckoutUrl}
          isEpayForm={payment?.isEpayForm ?? false}
          onClose={handleCloseWebView}
          onSuccess={handlePaymentSuccess}
          onCancel={handlePaymentCancel}
        />
      )}

      {/* ✅ Native: Payment Status Modal */}
      {Platform.OS !== "web" && (
        <PaymentStatusModal
          visible={showPaymentStatus}
          status={paymentStatusType}
          packageInfo={formatPackageInfoForModal()}
          onClose={handleClosePaymentStatus}
          autoDismiss={paymentStatusType === "success"}
          autoDismissDelay={3000}
        />
      )}
    </>
  );
}

const styles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.7)",
    justifyContent: "center",
    alignItems: "center",
  },

  // Make top bar a stacking context so absolute close button sits above modal body on web
  topButtons: {
    flexDirection: "row",
    justifyContent: "center",
    marginBottom: -2,
    position: "relative",
    zIndex: 20,
  },

  modalContainer: {
    maxHeight: "85%",
    backgroundColor: "#112345",
    borderRadius: 10,
    borderWidth: 1,
    borderColor: "#00f0ff",
    position: "relative",
    padding: 2,
    paddingTop: 0,
    marginTop: 0,
    textAlign: "center",
    margin: "auto",
    zIndex: 1,
  },

  scrollContainer: {
    flex: 1,
    width: "100%",
    textAlign: "center",
    margin: "auto",
  },
  scrollContent: {
    paddingBottom: 20,
    textAlign: "center",
    margin: "auto",
  },

  errorBox: {
    backgroundColor: "#331010",
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 20,
    alignSelf: "center",
    marginTop: 15,
    marginBottom: 20,
  },
  errorText: {
    color: "#ff7b7b",
    fontSize: 13,
    fontWeight: "500",
    textAlign: "center",
  },

  closeBtn: {
    width: 50,
    height: 50,
  },
  closeBtnWeb: {
    position: "absolute",
    top: 16,
    right: 10,
    zIndex: 9999,
  },
  closeBtnNative: {
    position: "relative",
    marginLeft: 10,
  },
  closeIcon: {
    width: 50,
    height: 50,
  },

  // --- Provider modal (web) ---
  providerOverlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.7)",
    justifyContent: "center",
    alignItems: "center",
    padding: 16,
  },
  providerBox: {
    width: 420,
    maxWidth: "95%",
    backgroundColor: "#112345",
    borderRadius: 14,
    borderWidth: 1,
    borderColor: "#00f0ff",
    padding: 18,
    position: "relative",
  },
  providerClose: {
    position: "absolute",
    right: 10,
    top: 10,
    width: 38,
    height: 38,
    zIndex: 10,
  },
  providerCloseIcon: {
    width: "100%",
    height: "100%",
  },
  providerTitle: {
    color: "white",
    fontSize: 16,
    fontWeight: "800",
    textAlign: "center",
    marginBottom: 12,
    marginTop: 6,
  },
  providerContinueBtn: {
    marginTop: 14,
    alignSelf: "center",
    backgroundColor: "#1f3c77",
    borderRadius: 10,
    paddingVertical: 10,
    paddingHorizontal: 18,
  },
  providerContinueText: {
    color: "white",
    fontWeight: "800",
    fontSize: 14,
  },
});

export default BuyPackagesModal;
